import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
    ConcentrationOrigin,
    DataQuality, PolygonType, QualityCriterionType, RemovalReason,
    RiskAssessmentStatus, ScreeningFlag, ScreeningLogInfo,
    ScreeningResultDetailView, ScreeningStandardparameters
} from 'src/app/contracts';
import { ScreeningService } from 'src/app/services';
import { BaseComponent } from 'src/app/shared/baseComponent';

@Component({
    selector: 'app-compound-datails',
    templateUrl: './compound-datails.component.html',
    styleUrls: ['./compound-datails.component.css']
})
export class CompoundDatailsComponent extends BaseComponent implements OnInit {
    public riskAssessmentStatus = RiskAssessmentStatus; // Make enum accessible from the html-template
    public polygonType = PolygonType;
    public removalReason = RemovalReason;
    public dataQuality = DataQuality;
    public screeningLogInfo = ScreeningLogInfo;
    public screeningResult: ScreeningResultDetailView = new ScreeningResultDetailView({});
    public standardParameters: ScreeningStandardparameters;
    public flags: ScreeningFlag = 0;
    public screeningFlag = ScreeningFlag;
    public qualityCriterionTypeEnum = QualityCriterionType;
    public concentrationOriginEnum = ConcentrationOrigin;

    constructor(
        private readonly route: ActivatedRoute,
        @Inject(LOCALE_ID) locale,
        private readonly screeningService: ScreeningService) {
        super(locale);
    }

    public ngOnInit(): void {
        const id = this.route.snapshot.paramMap.get('id');
        this.screeningService.getScreeningResultDetails(id).subscribe(res => {
            this.screeningResult = res;
            this.standardParameters = res.standardParameters;
            this.flags = res.flag;
        });
    }

    public removedInStep(screening: ScreeningResultDetailView, step: number): boolean {
        if (!screening) {
            return false;
        }

        switch (step) {
            case 0:
                return this.removedInSubStep(screening, 0, 1);
            case 1:
                return this.removedInSubStep(screening, 1, 1);
            case 2:
                return this.removedInSubStep(screening, 2, 1) || this.removedInSubStep(screening, 2, 2);
            case 3:
                return this.removedInSubStep(screening, 3, 1);
            default:
                console.log('Error. Called `removedInStep` with unknown step number: ' + step);
                return false;
        }
    }

    public removedBeforeStep(screening: ScreeningResultDetailView, step: number): boolean {
        for (let i = 0; i < step; i++) {
            if (this.removedInStep(screening, i)) {
                return true;
            }
        }
        return false;
    }

    public removedInSubStep(screening: ScreeningResultDetailView, step: number, substep: number): boolean {
        if (!screening) {
            return false;
        }

        if (step === 0) {
            if (substep === 1) {
                return screening.removed
                    && (screening.removalReason === RemovalReason.NotRemoved);

            }
        } else if (step === 1) {
            if (substep === 1) {
                return screening.removed
                    && (screening.removalReason === RemovalReason.Removed_0_1 ||
                        screening.removalReason === RemovalReason.Removed_0_2 ||
                        screening.removalReason === RemovalReason.Removed_1_1 ||
                        screening.removalReason === RemovalReason.Removed_1_2);
            }
        } else if (step === 2) {
            if (substep === 1) {
                return screening.removed
                    && (screening.removalReason === RemovalReason.Removed_2_1);

            } else if (substep === 2) {
                return screening.removed
                    && (screening.removalReason === RemovalReason.Removed_2_3
                        || screening.removalReason === RemovalReason.Removed_2_4);
            }
        } else if (step === 3) {
            if (substep === 1) {
                return screening.removed && screening.removalReason === RemovalReason.Removed_3_1;
            }
        }
        return false;
    }

    public stopAtSubStep(screening: ScreeningResultDetailView, step: number, substep: number): boolean {
        if (!screening) {
            return true;
        }

        if (step === 0) {
            if (substep === 1) {
                return screening.removed
                    && (screening.removalReason !== RemovalReason.NotRemoved);

            }
        } else if (step === 1) {
            if (substep === 1) {
                return screening.removed
                    && (screening.removalReason !== RemovalReason.Removed_0_1 &&
                        screening.removalReason !== RemovalReason.Removed_0_2 &&
                        screening.removalReason !== RemovalReason.Removed_1_1 &&
                        screening.removalReason !== RemovalReason.Removed_1_2);
            }
        } else if (step === 2) {
            if (substep === 1) {
                return true;
            } else if (substep === 2) {
                return screening.removed
                    && screening.removalReason !== RemovalReason.Removed_2_1;
            }
        } else if (step === 3) {
            if (substep === 1) {
                return screening.removed
                    && (screening.removalReason !== RemovalReason.Removed_3_1);
            }
        }
        return false;
    }

    public logContains(screening: ScreeningResultDetailView, logItem: ScreeningLogInfo): boolean {
        if (!screening || !screening.logInfo) {
            return false;
        }
        return screening.logInfo.indexOf(logItem) !== -1;
    }

    public hasFlag(flag: ScreeningFlag): boolean {
        // tslint:disable-next-line: no-bitwise
        return ((this.flags & flag) === flag);
    }

    public hasAnyFlags(): boolean {
        return this.flags !== 0;
    }
}
